<template>
  <div>
    <v-dialog v-model="exibirModal" max-width="500" persistent :style="{ zIndex: 9999 }">
      <v-card class="feedback-modal">
        <v-card-title class="d-flex align-center primary white--text">
          <v-icon color="white" class="mr-2">mdi-comment-text-outline</v-icon>
          <span class="text-h6">Ajude a melhorar o Medics</span>
          <v-spacer></v-spacer>
          <v-btn icon small color="white" @click="dispensarModal" class="ml-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="mb-4 text-body-2 grey--text text--darken-2">
            {{ $store.getters['sugestaoResposta/dadosCampanhaAtiva'].descricao }}
          </p>

          <p v-if="$store.getters['sugestaoResposta/configuracoesCampanha'].mostrarData">
            <i>Disponível de {{ formatarDataCampanha($store.getters['sugestaoResposta/dadosCampanhaAtiva'].dataInicio)
            }} até {{ formatarDataCampanha($store.getters['sugestaoResposta/dadosCampanhaAtiva'].dataFim) }}</i>
          </p>

          <v-textarea v-if="$store.getters['sugestaoResposta/configuracoesCampanha'].coletarResposta"
            v-model="comentario" label="Deixe sua sugestão ou comentário"
            placeholder="Descreva sua experiência, sugestões de melhorias ou relate algum problema..." rows="6"
            counter="2000" maxlength="2000" outlined color="primary" :rules="[regras.obrigatorio]" class="mb-3">
          </v-textarea>

          <v-rating v-if="$store.getters['sugestaoResposta/configuracoesCampanha'].coletarPontuacao" v-model="pontuacao"
            label="Avalie sua experiência geral" dense hover length="5" size="32" color="amber"
            background-color="grey lighten-1" class="mb-3">
          </v-rating>

          <div v-if="$store.getters['sugestaoResposta/configuracoesCampanha'].coletarPontuacao"
            class="text-caption grey--text mb-3">
            Avalie de 1 a 5 estrelas sua experiência geral com o sistema
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-btn text color="grey darken-1" @click="naoResponder" class="mr-2">
            Não quero responder
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" :loading="enviando" :disabled="isEnviarDisabled || enviando" @click="enviarSugestao"
            elevation="2"> <v-icon left>mdi-send</v-icon>
            {{ $store.getters['sugestaoResposta/configuracoesCampanha'].coletarResposta ? 'Enviar Sugestão' : 'Enviar Avaliação' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-tooltip left v-model="mostrarTooltip">
      <template v-slot:activator="{ on, attrs }">
        <div class="pulse-ring">
          <v-btn v-show="mostrarBotao" fab color="transparent" class="feedback-button" @click="abrirModal"
            v-bind="attrs" v-on="on">
            <img src="@/assets/images/robo.gif" alt="Feedback" width="60" height="60" style="border-radius: 50%;" />
          </v-btn>
        </div>
      </template>
      <span>Ajude a melhorar o Medics</span>
    </v-tooltip>

    <v-btn v-if="$route.query.feedback === 'reset'" fab small color="red" fixed bottom left @click="resetarFeedback"
      style="z-index: 9999; margin-bottom: 80px;">
      <v-icon>mdi-refresh</v-icon>
    </v-btn>

    <v-snackbar v-model="mostrarSnackbar" :color="tipoMensagem" timeout="4000" top right>
      {{ mensagem }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="mostrarSnackbar = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { mapGetters, mapActions } from 'vuex';
import { SugestaoRespostaService } from '@/services/SugestaoRespostaService';
import { decode, getToken } from '@/utils/Token';
import { AdicionarSugestaoRespostaInputType } from '@/types/sugestoesRespostas/AdicionarSugestaoRespostaInputType';
import { MensagemMixin } from '@/helpers/MensagemHelper';

interface IAuthUserModel {
  id?: string;
  nomeCompleto?: string;
  profilePicture?: string;
}

@Component({
  name: 'FeedbackModal',
  computed: {
    ...mapGetters('sugestaoResposta', [
      'temCampanhaAtiva',
      'configuracoesCampanha',
      'dadosCampanhaAtiva',
      'estaCarregando',
    ]),
  },
  methods: {
    ...mapActions('sugestaoResposta', [
      'ocultarCampanha',
      'resetarEstado',
    ]),
  },
  watch: {
    temCampanhaAtiva: {
      handler(novoValor: boolean, valorAnterior: boolean) {
        if (novoValor !== valorAnterior) {
          this.atualizarVisibilidadeBotao();
        }
      },
      immediate: false,
    },
    campanhaAtual: {
      handler(novaCampanha: any, campanhaAnterior: any) {
        const novaId = novaCampanha?.id;
        const idAnterior = campanhaAnterior?.id;

        if (novaId !== idAnterior) {
          this.atualizarVisibilidadeBotao();
        }
      },
      immediate: false,
    },
  },
})

export default class FeedbackModal extends Vue {
  private exibirModal: boolean = false;

  private comentario: string = '';

  private pontuacao: number = 0;

  private enviando: boolean = false;

  private modalDispensada: boolean = false;

  private botaoOculto: boolean = false;

  private mostrarTooltip: boolean = true;

  private sugestaoRespostaService: SugestaoRespostaService = new SugestaoRespostaService();

  private mostrarSnackbar: boolean = false;

  private mensagem: string = '';

  private tipoMensagem: string = 'success';

  private readonly regras = {
    obrigatorio: (mensagemFeedbackUsuario: string): boolean | string => !!mensagemFeedbackUsuario?.trim()
      || 'Este campo é obrigatório',
  };

  get mostrarBotao(): boolean {
    const temCampanha = this.$store.getters['sugestaoResposta/temCampanhaAtiva'];
    const campanhaAtiva = this.$store.getters['sugestaoResposta/dadosCampanhaAtiva'];
    const campanhaDispensada = campanhaAtiva ? this.campanhaFoiDispensada(campanhaAtiva.id) : false;
    return temCampanha
      && !this.exibirModal
      && !this.botaoOculto
      && !this.modalDispensada
      && !campanhaDispensada;
  }

  get campanhaAtual(): any {
    return this.$store.getters['sugestaoResposta/dadosCampanhaAtiva'];
  }

  get isEnviarDisabled(): boolean {
    const configuracoes = this.$store.getters['sugestaoResposta/configuracoesCampanha'];
    const coletarResposta = configuracoes?.coletarResposta;
    const coletarPontuacao = configuracoes?.coletarPontuacao;

    if (coletarResposta && !this.comentario.trim()) {
      return true;
    }

    if (!coletarResposta && coletarPontuacao && this.pontuacao > 0) {
      return false;
    }

    if (!coletarResposta && !coletarPontuacao) {
      return false;
    }

    return coletarResposta && !this.comentario.trim();
  }

  private obterChaveDispensaCampanha(campanhaId?: number): string {
    if (!campanhaId) {
      const campanha = this.$store.getters['sugestaoResposta/dadosCampanhaAtiva'];
      campanhaId = campanha?.id;
    }

    return campanhaId ? `feedback-modal-dispensado-campanha-${campanhaId}` : 'feedback-modal-dispensado';
  }

  private campanhaFoiDispensada(campanhaId?: number): boolean {
    const chave = this.obterChaveDispensaCampanha(campanhaId);
    return !!sessionStorage.getItem(chave);
  }

  private dispensarCampanhaAtual(campanhaId?: number): void {
    const chave = this.obterChaveDispensaCampanha(campanhaId);
    sessionStorage.setItem(chave, 'true');
  }

  private limparDispensasAntigas(): void {
    const keys = Object.keys(sessionStorage);
    const feedbackKeys = keys.filter((key) => key.startsWith('feedback-modal-dispensado-campanha-'));

    if (feedbackKeys.length > 10) {
      feedbackKeys.slice(0, feedbackKeys.length - 10).forEach((key) => { sessionStorage.removeItem(key); });
    }
  }

  async mounted(): Promise<void> {
    const campanhaAtiva = this.$store.getters['sugestaoResposta/dadosCampanhaAtiva'];
    const campanhaDispensada = campanhaAtiva ? this.campanhaFoiDispensada(campanhaAtiva.id) : false;

    if (campanhaDispensada) {
      this.botaoOculto = true;
      this.modalDispensada = true;
    } else {
      this.atualizarVisibilidadeBotao();
    }

    this.limparDispensasAntigas();
  }

  public abrirModal(): void {
    this.mostrarTooltip = false;
    this.exibirModal = true;
    this.comentario = '';
    this.pontuacao = 0;
  }

  private dispensarModal(): void {
    this.exibirModal = false;
    this.comentario = '';
    this.pontuacao = 0;

    setTimeout(() => {
      this.mostrarTooltip = true;
    }, 300);
  }

  private naoResponder(): void {
    const campanhaAtiva = this.$store.getters['sugestaoResposta/dadosCampanhaAtiva'];

    this.modalDispensada = true;
    this.botaoOculto = true;
    this.exibirModal = false;
    this.comentario = '';
    this.pontuacao = 0;

    if (campanhaAtiva?.id) {
      this.dispensarCampanhaAtual(campanhaAtiva.id);
    }

    this.$emit('modal-dispensado', { campanhaId: campanhaAtiva?.id });
  }

  private async enviarSugestao(): Promise<void> {
    const configuracoes = this.$store.getters['sugestaoResposta/configuracoesCampanha'];
    const coletarResposta = configuracoes?.coletarResposta;

    if (coletarResposta && !this.comentario.trim()) {
      return;
    }

    this.enviando = true;

    try {
      const token = getToken();
      const user: IAuthUserModel | null = token ? decode(token) : null;
      const campanhaAtiva = this.$store.getters['sugestaoResposta/dadosCampanhaAtiva'];

      if (!campanhaAtiva?.id) {
        MensagemMixin(8000, 'error', 'Nenhuma campanha ativa encontrada');
        return;
      }

      if (!user?.id) {
        MensagemMixin(8000, 'error', 'Usuário não identificado');
      }

      const sugestaoRespostaData: AdicionarSugestaoRespostaInputType = {
        sugestaoCampanhaId: campanhaAtiva.id,
        usuarioId: user.id,
        resposta: this.comentario.trim() || '',
        dataResposta: new Date(),
        pontuacao: this.$store.getters['sugestaoResposta/configuracoesCampanha']?.coletarPontuacao ? this.pontuacao : 0,
      };

      const respostaFeedback = await this.sugestaoRespostaService.adicionarSugestaoResposta(sugestaoRespostaData);

      if (!respostaFeedback) {
        MensagemMixin(8000, 'error', 'Erro ao enviar sugestão. Tente novamente.');
        return;
      }

      this.$emit('sugestao-enviada', {
        ...sugestaoRespostaData,
        id: campanhaAtiva.id,
        usuario: user ? `${user.nomeCompleto}` : 'Usuário Anônimo',
        timestamp: new Date().toISOString(),
      });

      MensagemMixin(8000, 'success', 'Obrigado! Sua sugestão foi enviada com sucesso.');
      this.modalDispensada = true;
      this.botaoOculto = true;
      this.exibirModal = false;
      this.comentario = '';
      this.pontuacao = 0;

      if (campanhaAtiva?.id) {
        this.dispensarCampanhaAtual(campanhaAtiva.id);
      }
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
      MensagemMixin(8000, 'error', 'Erro ao enviar sugestão. Tente novamente.');
    } finally {
      this.enviando = false;
    }
  }

  private formatarDataCampanha(data: string): string {
    if (!data) {
      return '';
    }

    try {
      const dataObj = new Date(data);
      return dataObj.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      console.error('Erro ao formatar data da campanha:', error);
      return '';
    }
  }

  private atualizarVisibilidadeBotao(): void {
    const campanhaAtiva = this.$store.getters['sugestaoResposta/dadosCampanhaAtiva'];
    const temCampanha = this.$store.getters['sugestaoResposta/temCampanhaAtiva'];
    const campanhaDispensada = campanhaAtiva ? this.campanhaFoiDispensada(campanhaAtiva.id) : false;

    if (campanhaDispensada) {
      this.botaoOculto = true;
      this.modalDispensada = true;
    } else if (temCampanha) {
      this.botaoOculto = false;
      this.modalDispensada = false;
      this.mostrarTooltip = false;
    } else {
      this.botaoOculto = true;
      this.modalDispensada = false;
    }

    this.$nextTick(() => {
      this.$forceUpdate();
    });
  }

  private resetarFeedback(): void {
    const campanhaAtiva = this.$store.getters['sugestaoResposta/dadosCampanhaAtiva'];

    if (campanhaAtiva?.id) {
      const chave = this.obterChaveDispensaCampanha(campanhaAtiva.id);
      sessionStorage.removeItem(chave);
    }

    sessionStorage.removeItem('feedback-modal-dispensado');
    this.botaoOculto = false;
    this.modalDispensada = false;
    this.mostrarTooltip = true;
    this.exibirModal = false;
    MensagemMixin(8000, 'info', 'Feedback resetado! O botão deve aparecer.');
  }
}
</script>

<style scoped>
.feedback-modal {
  border-radius: 18px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
  margin: 23px auto !important;
}

.feedback-button {
  animation: none !important;
}

.pulse-ring {
  position: relative;
  display: block;
  margin: 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  animation: pulse-ring-animation 2s infinite;
}

.pulse-ring .feedback-button {
  position: absolute !important;
  top: -12px !important;
  left: -12px !important;
  z-index: 10 !important;
}

.feedback-button.v-btn.v-btn--fab {
  position: relative !important;
  display: block !important;
  margin: 0 !important;
  animation: none !important;
  box-shadow: none !important;
  transition: all 0.3s ease !important;
}

.feedback-button.v-btn.v-btn--fab:hover {
  animation-play-state: infinite !important;
  background-color: transparent !important;
}

.v-application .elevation-6 {
  box-shadow: none !important;
}

.v-dialog {
  z-index: 1050 !important;
}

.v-overlay {
  z-index: 1040 !important;
}

.feedback-modal .v-card__title {
  background: linear-gradient(135deg, #2e4b9dcc 0%, #1565c0 100%);
  border-radius: 12px 12px 0 0 !important;
}

.v-btn__content>i {
  margin: 0px;
}

@keyframes pulse-ring-animation {
  0% {
    transform: scale(0.8);
    background-color: rgba(16, 168, 96, 0.8);
    box-shadow: 0 0 0 0 rgba(16, 168, 96, 0.8);
  }

  50% {
    transform: scale(0.9);
    background-color: rgba(16, 168, 96, 0.3);
    box-shadow: 0 0 0 15px rgba(16, 168, 96, 0.3);
  }

  100% {
    transform: scale(0.8);
    background-color: rgba(16, 168, 96, 0);
    box-shadow: 0 0 0 25px rgba(16, 168, 96, 0);
  }
}

.v-tooltip__content.menuable__content__active {
  background: rgba(16, 168, 96, 0.8);
  left: calc(100% - 350px) !important;
}

.feedback-button img {
  object-fit: cover;
  filter: brightness(1.1) contrast(1.1);
}
</style>
